// Campaña agent-noise-collapse — los DOS clasificadores del ruido que un
// agente principal genera mientras espera a ayudantes en segundo plano.
//
// Provenance: Santi, 2026-09-13 — "¿Estás trabajando? Estoy un poco
// confuso". Con 37 lanzamientos en segundo plano en 4 h, la conversación
// se llenó de (a) informes internos crudos y (b) burbujas repetidas que
// sólo decían "No response requested.".
//
// PURO Y SIN REACT a propósito: un clasificador que se pueda probar sin
// montar un DOM es un clasificador que se prueba. `ConversationList` es
// el único consumidor; los tests unitarios atacan estas funciones
// directamente con las formas reales del cable.
//
// De dónde sale cada forma (verificado contra `session-manager.ts` y los
// transcripts de este runner, tabla completa en
// `docs/campaigns/agent-noise-collapse.md`):
//
//   • `<task-notification>` llega como evento `user` con `content` string.
//     El backend NO persiste el cuerpo (`handleUser` cierra el job y sale
//     por `if (!Array.isArray(content)) return`), así que hoy no llega al
//     chat. El clasificador lo cubre igual: es una defensa de una línea
//     contra un cambio de la CLI, no una suposición sobre ella.
//   • "No response requested." llega como `assistant_text` verbatim. Ése
//     sí se pinta hoy, una burbuja por notificación recibida.
//   • Los informes de ayudante llegan como `assistant_text` HIJO
//     (`parentToolUseId`). Se pintan dentro del `SubagentPanel`… salvo
//     cuando el `tool_use` padre cayó fuera de la ventana inicial del
//     historial (`listAgentInitialWindow`, mín. 200 / tope 1000 filas):
//     entonces `groupBySubagent` los re-aloja en `topLevel` y salen como
//     burbujas crudas. ÉSA es la fuente real de los muros que vio Santi.

import type { Message } from '../../types';

/** Tope del resumen de una línea (contrato, resultado 1). */
export const NOTIFICATION_SUMMARY_MAX = 120;

/**
 * Prefijos que marcan "esto es una notificación máquina-a-máquina, no
 * prosa para el founder". Lista CERRADA y ordenada por frecuencia real.
 *
 * Se comparan contra el texto YA recortado (`trimStart`), porque la CLI
 * antepone saltos de línea a varias de estas formas.
 */
const NOTIFICATION_PREFIXES: readonly string[] = [
  '<task-notification>',
  '<system-reminder>',
  '<agent-message',
  '<cross-session-message',
  '<local-command-stdout>',
  'Another Claude session sent a message:',
];

/**
 * Resultado 3 — el turno vacío.
 *
 * La CLI cierra con "No response requested." cada turno que sólo existía
 * para consumir una notificación de un hijo. Santi vio 25 de estos
 * seguidos: veinticinco burbujas que no dicen nada.
 *
 * Tolerante a espacios (colapsa cualquier blanco interno), sin distinguir
 * mayúsculas, y el punto final es opcional — las tres variantes se han
 * observado. NO se acepta ningún texto adicional: un turno que dice eso
 * Y ALGO MÁS es un turno con contenido y se pinta entero.
 */
export function isEmptyTurnText(text: string): boolean {
  const normalized = text.trim().replace(/\s+/g, ' ').toLowerCase();
  return normalized === 'no response requested' || normalized === 'no response requested.';
}

/** Lo que la fila plegada necesita para pintarse. */
export interface NotificationClassification {
  /**
   * `helper` → "Informe interno de <helperName>" (o "de un ayudante" si
   * el nombre no viaja). `system` → "Aviso del sistema".
   */
  kind: 'helper' | 'system';
  /** Nombre del ayudante cuando el texto lo trae; si no, `null`. */
  helperName: string | null;
  /** Resumen de UNA línea, ya recortado a `NOTIFICATION_SUMMARY_MAX`. */
  summary: string;
}

/**
 * `<summary>Agent "reviewer" completed…</summary>` — la CLI mete el
 * nombre entre comillas rectas o tipográficas según versión.
 */
const AGENT_NAME_RE = /Agent\s+["“”']([^"“”']+)["“”']/;
/** `<agent-message from-name="X">` / `from="X"` — atribución de flota. */
const FROM_NAME_RE = /\bfrom(?:-name)?=["']([^"']+)["']/;
/** El cuerpo de `<summary>…</summary>`, que es el resumen que ya nos dan. */
const SUMMARY_TAG_RE = /<summary>([\s\S]*?)<\/summary>/;

/**
 * Primera frase del cuerpo, recortada. "Frase" = hasta el primer `.`,
 * `!`, `?` o salto de línea, lo que llegue antes.
 *
 * Se quitan antes las etiquetas XML-ish de la primera línea: un resumen
 * que empieza por `<task-notification>` no es un resumen, es la etiqueta.
 */
export function firstSentence(body: string, max: number = NOTIFICATION_SUMMARY_MAX): string {
  const cleaned = body
    // Etiquetas de una sola pieza (`<task-notification>`, `</status>`, …).
    .replace(/<\/?[a-zA-Z][^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned === '') return '';
  const match = /^(.*?)(?:[.!?](?:\s|$)|$)/.exec(cleaned);
  const sentence = (match?.[1] ?? cleaned).trim();
  const picked = sentence === '' ? cleaned : sentence;
  if (picked.length <= max) return picked;
  // Corte por palabra cuando se puede: `…` en mitad de una palabra se lee
  // como un error de la app, no como un recorte.
  const hard = picked.slice(0, max - 1);
  const lastSpace = hard.lastIndexOf(' ');
  const base = lastSpace > max / 2 ? hard.slice(0, lastSpace) : hard;
  return `${base.trimEnd()}…`;
}

/**
 * Resultados 1 y 2 — ¿este texto es una notificación interna?
 *
 * Devuelve `null` cuando NO lo es (la abrumadora mayoría de las filas:
 * el coste de esta función en el camino caliente es un `startsWith` por
 * prefijo sobre una lista de seis).
 */
export function classifyNotification(text: string): NotificationClassification | null {
  const trimmed = text.trimStart();
  const matched = NOTIFICATION_PREFIXES.some((p) => trimmed.startsWith(p));
  if (!matched) return null;

  const summaryTag = SUMMARY_TAG_RE.exec(trimmed)?.[1]?.trim();
  // El `<summary>` de la CLI es EXACTAMENTE el resumen de una línea que
  // queremos; cuando falta, la primera frase del cuerpo es lo honesto.
  const summarySource = summaryTag !== undefined && summaryTag !== '' ? summaryTag : trimmed;
  const summary = firstSentence(summarySource);

  // El nombre se busca en el `<summary>` primero (`Agent "X" completed`)
  // y luego en los atributos de `<agent-message …>`.
  const helperName =
    AGENT_NAME_RE.exec(trimmed)?.[1]?.trim() || FROM_NAME_RE.exec(trimmed)?.[1]?.trim() || null;

  // EL NOMBRE DECIDE, NO LA FORMA (revisor, issue 2). Un `<task-notification>`
  // SIN `Agent "X"` es el cierre de un `Bash` en segundo plano — no hay
  // ningún ayudante detrás —, así que titularlo "Informe interno de un
  // ayudante" sería copy falsa, y la copy falsa en la capa founder es
  // exactamente lo que founder-first §5 prohíbe. Sin autor ⇒ aviso del
  // sistema. `helperAnonymous` queda SÓLO para el huérfano del resultado 2,
  // donde sí hay un ayudante y lo que falta es su nombre.
  return {
    kind: helperName === null ? 'system' : 'helper',
    helperName,
    summary,
  };
}

// ── Capa de fila ─────────────────────────────────────────────────────
//
// Lo de arriba es puro string (y se prueba como tal). Lo de aquí abajo
// traduce UNA fila del stream a UNA decisión de render. `ConversationList`
// es el único consumidor y sólo la consulta en MODO FOUNDER: en modo
// developer la verdad cruda se conserva byte a byte, que es la mitad del
// contrato de dos capas (founder-first §3).

/** El texto founder-visible de una fila, o `null` si la fila no lleva. */
export function rowText(m: Message): string | null {
  if (m.type !== 'assistant_text' && m.type !== 'user') return null;
  const c = m.content as { text?: unknown } | null;
  return typeof c?.text === 'string' ? c.text : null;
}

export type NoiseVerdict =
  /** No se pinta nada (turno vacío, o plomería huérfana de un ayudante). */
  | { kind: 'hide' }
  /** Se pinta UNA fila plegada. `classification` `null` = huérfano sin
   *  forma de notificación: título genérico, resumen de la 1ª frase. */
  | { kind: 'report'; classification: NotificationClassification | null; raw: string }
  /** `null` = fila normal, se pinta como siempre. */
  | null;

/**
 * @param isOrphan `true` si la fila lleva `parentToolUseId` y ese padre NO
 *   está en el historial cargado (`groupBySubagent().orphanIds`). Un
 *   huérfano es, por definición, la salida interna de un ayudante que el
 *   `SubagentPanel` habría plegado si el padre siguiera cargado; que la
 *   ventana del historial lo haya perdido no lo convierte en prosa para
 *   el founder.
 */
export function classifyNoiseRow(m: Message, isOrphan: boolean): NoiseVerdict {
  const text = rowText(m);

  if (text === null) {
    // Plomería huérfana: `tool_use` / `tool_result` / `thinking_block`
    // sueltos de un ayudante cuyo panel ya no existe. Sin el panel no
    // tienen ni contexto ni cabecera; son ruido puro.
    return isOrphan ? { kind: 'hide' } : null;
  }

  // Resultado 3 — el turno que sólo existía para consumir una
  // notificación. Cero filas, ni siquiera una leyenda: N seguidos tienen
  // que dejar la conversación intacta.
  if (m.type === 'assistant_text' && isEmptyTurnText(text)) return { kind: 'hide' };

  // Resultado 1 — forma de notificación, venga por donde venga.
  const classification = classifyNotification(text);
  if (classification !== null) return { kind: 'report', classification, raw: text };

  // Resultado 2 — huérfano con prosa: el informe del ayudante. Se pliega
  // igual, con título genérico porque el texto no se identifica solo.
  if (isOrphan && m.type === 'assistant_text') {
    return { kind: 'report', classification: null, raw: text };
  }

  return null;
}
